import {
  Component,
  Input,
  OnInit,
  WritableSignal,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { FormlyFieldConfig, FormlyModule } from '@ngx-formly/core';
import { TranslationService } from '../../../core/services/translation.service';
import { TranslatePipe } from '../../../shared/pipes/translate.pipe';

export interface SaveReportAsModel {
  name: string;
}

/**
 * Name-entry dialog for "Save As". Saving as always creates a brand-new
 * report, so the chosen name must not collide with one the user can already
 * see — see `ReportBuilderComponent.saveAs` / `ReportResultsComponent.saveAs`.
 */
@Component({
  selector: 'app-save-report-as-dialog',
  standalone: true,
  imports: [ReactiveFormsModule, FormlyModule, TranslatePipe],
  templateUrl: './save-report-as-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './save-report-as-dialog.component.css',
})
export class SaveReportAsDialogComponent implements OnInit {
  private translate = inject(TranslationService);

  @Input({ required: true }) modelSignal!: WritableSignal<SaveReportAsModel>;
  @Input({ required: true }) existingNames: string[] = [];
  @Input() saveDisabledSignal?: WritableSignal<string | null>;

  form = new FormGroup({});
  model: SaveReportAsModel = { name: '' };
  fields: FormlyFieldConfig[] = [
    {
      key: 'name',
      type: 'input',
      props: {
        label: this.translate.translate('reports.saveAsDialog.field.name'),
        required: true,
      },
    },
  ];

  ngOnInit(): void {
    this.model = { ...this.modelSignal() };
    this.updateSaveDisabled();
  }

  onModelChange(updated: SaveReportAsModel): void {
    this.model = updated;
    this.modelSignal.set(this.model);
    this.updateSaveDisabled();
  }

  private isNameTaken(name: string): boolean {
    const trimmed = name.trim().toLowerCase();
    return this.existingNames.some((n) => n.trim().toLowerCase() === trimmed);
  }

  private updateSaveDisabled(): void {
    if (!this.saveDisabledSignal) return;
    const name = this.model.name?.trim() ?? '';
    if (!name) {
      this.saveDisabledSignal.set(
        this.translate.translate('reports.saveAsDialog.validation.required'),
      );
    } else if (this.isNameTaken(name)) {
      this.saveDisabledSignal.set(
        this.translate.translate('reports.saveAsDialog.validation.notUnique'),
      );
    } else {
      this.saveDisabledSignal.set(null);
    }
  }
}
